import * as React from "react";
import { UsersList } from "./UsersList.component";
import { IUserInfo } from "../types/common";
import { useUserContext } from "../../context/users/UserContext";
import { PageContextProvider } from "../../context/numbered-pagination";
import { Count, CustomActionRow } from "../../components/utils/Pagination/NumberedPagination/common";
import AccountsPagination from "../../components/utils/Pagination/AccountsPagination";
import { usePagination } from "../../components/utils/Pagination/hooks";
import { pluralize } from "../../utils";
import { Header as H1 } from "../../components/utils/Headings";
import { AddButton } from "../Common/Components/FormComponents";
import { FaPlus } from "react-icons/fa";
import styled from "styled-components";
import { api } from "../../dependencies";
import Error from "../../components/pages/Error";
import Spinner from "../../components/utils/Spinner";

const CustomAddButton = styled(AddButton)`
  width: 17rem;
`;

const StyledPlus = styled(FaPlus)`
  margin-right: 0.5rem;
`;

export const UsersListWrapper: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { usersIds, deleteUser, addNewUser } = useUserContext();

  const [requestNumber, setRequestNumber] = React.useState<number>(0);
  const [usersInfo, setUsersInfo] = React.useState<IUserInfo[]>([]);
  const [error, setError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [usersPerPage, setUsersPerPage] = React.useState<number>(50);

  const loadUsers = async (requestBatchStart: number, requestBatchEnd: number) => {
    try {
      const requestIds = usersIds.slice(requestBatchStart, requestBatchEnd);
      const res = await api.users.getUsersInfo(accountId, requestIds);
      return res;
    } catch (error) {
      return setError(true);
    }
  };

  const loadMoreUsers = async () => {
    setIsLoading(true);
    const newRequestNumber = requestNumber + 1;
    const requestBatchStart = requestNumber * usersPerPage;
    const requestBatchEnd = newRequestNumber * usersPerPage;
    const res = await loadUsers(requestBatchStart, requestBatchEnd);
    setUsersInfo(prevUsersInfo => [...prevUsersInfo, ...res]);
    setRequestNumber(newRequestNumber);
    setIsLoading(false);
  };

  const loadMoreUsersPerPage = async (newPerPage: number) => {
    setIsLoading(true);
    const res = await loadUsers(0, newPerPage);
    setUsersInfo(res);
    setIsLoading(false);
  };

  const loadInitialUsers = async () => {
    setIsLoading(true);
    const res = await loadUsers(0, usersPerPage);
    setRequestNumber(1);
    setUsersInfo(res);
    setIsLoading(false);
  };

  React.useEffect(() => {
    loadInitialUsers();
  }, []);

  const total = usersIds.length;
  const { value, pagedData } = usePagination(usersInfo, usersPerPage);

  if (error) {
    return <Error />;
  }

  const handleUsersPerPageChange = (newPerPage: number) => {
    setRequestNumber(1);
    setUsersPerPage(newPerPage);
    loadMoreUsersPerPage(newPerPage);
  };

  const handleLoadMoreUsers = () => {
    loadMoreUsers();
    setRequestNumber(prevRequestNumber => prevRequestNumber + 1);
  };

  return (
    <PageContextProvider value={value}>
      <H1>Accounts</H1>
      <CustomActionRow>
        <CustomAddButton onClick={addNewUser}>
          <StyledPlus />
          Create New Account
        </CustomAddButton>
        <AccountsPagination
          total={total}
          isLoading={isLoading}
          handleLoadMoreUsers={handleLoadMoreUsers}
          handleUsersPerPageChange={handleUsersPerPageChange}
        />
        <Count>{pluralize("Account", total)}</Count>
      </CustomActionRow>
      {isLoading ? <Spinner /> : <UsersList usersInfo={pagedData} deleteUser={deleteUser} />}
    </PageContextProvider>
  );
};
